import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { DollarSign, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import {
  useFinancialSummary,
  useUpsertFinancialSummary,
  useTransactions,
  useCreateTransaction,
  useUpdateTransaction,
  useDeleteTransaction,
  useUploadCheckFile,
  useDownloadCheckFile,
  useDeleteCheckFile,
} from '@/hooks/use-claim-financials';
import { SummaryTab } from './financials/SummaryTab';
import { PaymentsTab } from './financials/PaymentsTab';
import { DeletePaymentDialog } from './financials/DeletePaymentDialog';
import type {
  CreateTransactionRequest,
  UpdateTransactionRequest,
  UpsertFinancialSummaryRequest,
  ClaimTransaction,
} from '@/api/generated/client/models';

// ── Types ─────────────────────────────────────────────

interface FinancialsModuleProps {
  claimId: number;
  isOpen: boolean;
  onToggle: () => void;
  canWrite: boolean;
}

type TabKey = 'summary' | 'payments';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'summary', label: 'Summary' },
  { key: 'payments', label: 'Payments' },
];

// ── Component ─────────────────────────────────────────

export function FinancialsModule({ claimId, isOpen, onToggle, canWrite }: FinancialsModuleProps) {
  const { data: summary, isLoading: summaryLoading } = useFinancialSummary(claimId);
  const { data: transactions = [], isLoading: transactionsLoading } = useTransactions(claimId);

  const upsertSummary = useUpsertFinancialSummary(claimId);
  const createTransaction = useCreateTransaction(claimId);
  const updateTransaction = useUpdateTransaction(claimId);
  const deleteTransaction = useDeleteTransaction(claimId);
  const uploadCheckFile = useUploadCheckFile(claimId);
  const downloadCheckFile = useDownloadCheckFile(claimId);
  const deleteCheckFileMut = useDeleteCheckFile(claimId);

  const [activeTab, setActiveTab] = useState<TabKey>('summary');
  const [editingTransactionId, setEditingTransactionId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<ClaimTransaction | null>(null);

  const isLoading = summaryLoading || transactionsLoading;

  // ── Handlers with check-file upload orchestration ───

  const handleCreate = useCallback(
    async (data: CreateTransactionRequest, checkFile?: File) => {
      const created = await createTransaction.mutateAsync(data);
      if (checkFile && created?.id) {
        await uploadCheckFile.mutateAsync({ transactionId: created.id, file: checkFile });
      }
    },
    [createTransaction, uploadCheckFile],
  );

  const handleUpdate = useCallback(
    async (id: number, data: UpdateTransactionRequest, checkFile?: File, removeExistingCheckFile?: boolean) => {
      await updateTransaction.mutateAsync({ id, data });
      if (checkFile) {
        await uploadCheckFile.mutateAsync({ transactionId: id, file: checkFile });
      } else if (removeExistingCheckFile) {
        await deleteCheckFileMut.mutateAsync(id);
      }
      setEditingTransactionId(null);
    },
    [updateTransaction, uploadCheckFile, deleteCheckFileMut],
  );

  const handleDeleteClick = useCallback(
    (id: number) => {
      const transaction = transactions.find(t => t.id === id);
      if (transaction) {
        setTransactionToDelete(transaction);
        setDeleteDialogOpen(true);
      }
    },
    [transactions],
  );

  const handleDeleteConfirm = useCallback(() => {
    if (transactionToDelete?.id) {
      deleteTransaction.mutate(transactionToDelete.id);
      setTransactionToDelete(null);
    }
  }, [transactionToDelete, deleteTransaction]);

  return (
    <Card className="shadow-xs w-full max-w-full overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        <CollapsibleTrigger className="w-full">
          <CardHeader className="py-3 px-4 hover:bg-muted/50 cursor-pointer transition-colors overflow-hidden">
            <div className="flex items-center justify-between min-w-0 w-full">
              <div className="flex items-center gap-2 min-w-0">
                <DollarSign className="h-4 w-4 shrink-0 text-green-600" />
                <CardTitle className="text-sm font-medium truncate">Financials</CardTitle>
              </div>
              {isOpen
                ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent className="overflow-hidden">
          <div className="w-0 min-w-full">
          <CardContent className="px-4 pb-4 pt-0 overflow-hidden">
            {/* Tab bar */}
            <div className="border-b mb-3 -mx-4 px-4">
              <div className="flex justify-center gap-4">
                {TABS.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`shrink-0 px-2.5 py-1.5 text-[11px] font-medium whitespace-nowrap transition-colors ${
                      activeTab === key
                        ? 'border-b-2 border-primary text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {isLoading ? (
              <div className="flex justify-center p-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="-mx-1 px-1">
                {activeTab === 'summary' && (
                  <SummaryTab
                    summary={summary ?? null}
                    transactions={transactions}
                    onSave={(data: UpsertFinancialSummaryRequest) => upsertSummary.mutate(data)}
                    canWrite={canWrite}
                  />
                )}
                {activeTab === 'payments' && (
                  <PaymentsTab
                    transactions={transactions}
                    onCreate={handleCreate}
                    onUpdate={handleUpdate}
                    onDelete={handleDeleteClick}
                    onDownloadCheckFile={(id: number, filename?: string) => downloadCheckFile.mutate({ transactionId: id, filename })}
                    isCreating={createTransaction.isPending || uploadCheckFile.isPending}
                    isUpdating={updateTransaction.isPending || uploadCheckFile.isPending || deleteCheckFileMut.isPending}
                    editingId={editingTransactionId}
                    setEditingId={setEditingTransactionId}
                    canWrite={canWrite}
                  />
                )}
              </div>
            )}
          </CardContent>
          </div>
        </CollapsibleContent>
      </Collapsible>

      <DeletePaymentDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
      />
    </Card>
  );
}
